/* eslint-disable complexity */
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { ExamDayRatingType } from '@ils/static/enums.js';
import { DateTime } from 'luxon';
import { ExamDay } from '../examdays.js';

/**
 * @entity ExamDayFilter
 */
@Entity()
export class ExamDayFilter extends AdornisFilter {
  static override _class = 'ExamDayFilter';

  constructor(doc: PartialEntityData<ExamDayFilter>) {
    super(doc);
  }

  @Field(type => [ExamDay])
  examDays: Maybe<ExamDay[]>;

  @Field(type => [Int])
  months?: number[] | null;

  @Field(type => Int)
  year?: number | null;

  @Field(type => Boolean)
  isPublished: Maybe<boolean>;

  @Field(type => Boolean)
  isPublishedForTesters: Maybe<boolean>;

  @Field(type => Boolean)
  isPlanned: Maybe<boolean>;

  @Field(type => Boolean)
  hasFallbackTesters: Maybe<boolean>;

  @Field(type => Boolean)
  hasCandidates: Maybe<boolean>;

  @Field(type => String)
  ratingType: Maybe<ExamDayRatingType>;

  @Field(type => ID)
  examCenterID: Maybe<string>;

  @Field(type => ID)
  locationID: Maybe<string>;

  // todo: implement
  // @Field(type => Boolean)
  // hasSupervisor: Maybe<boolean>;

  addBooleanSelector(selector, prop, examDayField): void {
    if (typeof prop === 'boolean') {
      selector.$and ||= [];

      if (prop) {
        selector.$and.push({ [examDayField]: { $eq: true } });
      } else {
        selector.$and.push({ [examDayField]: { $ne: true } });
      }
    }
  }

  override getMongoFilter(): Record<string, any> {
    // mitigate an error where this has props that are not in the gql set
    const me = this.toObject();

    const selector: Dictionary<any> = super.getMongoFilter();

    selector.$and ||= [];

    selector.$and.push({ archived: false });

    if (Array.isArray(this.months) && this.months.length) {
      selector.$and ||= [];

      // Create an array of date range objects for each month
      const dateRanges = this.months.map(month => {
        const startDate = DateTime.fromObject({ year: this.year, month: month + 1, day: 1 });
        const endDate = startDate.endOf('month');
        return { start: startDate.toJSDate(), end: endDate.toJSDate() };
      });

      selector.$and.push({
        $or: dateRanges.map(range => ({
          start: { $gte: range.start, $lte: range.end },
        })),
      });
    }
    if (this.year) {
      const startDate = DateTime.fromObject({ year: this.year });

      selector.$and ||= [];

      selector.$and.push({
        start: {
          $gte: startDate.startOf('year').toJSDate(),
          $lte: startDate.endOf('year').toJSDate(),
        },
      });

      this.addBooleanSelector(selector, me.isPublished, 'published');
      this.addBooleanSelector(selector, me.isPublishedForTesters, 'isPublishedForTesters');

      if (typeof me.isPlanned === 'boolean') {
        selector.$and ||= [];
        selector.$and.push({
          'slotIDs.1': { $exists: me.isPlanned },
        });
      }

      if (typeof me.hasFallbackTesters === 'boolean') {
        selector.$and ||= [];

        me.hasFallbackTesters
          ? selector.$and.push({
              fallbackTesters: {
                $elemMatch: {
                  $exists: true,
                  $ne: null,
                },
              },
            })
          : selector.$and.push({
              $or: [
                { fallbackTesters: { $exists: false } },
                { fallbackTesters: { $size: 0 } },
                { fallbackTesters: { $not: { $elemMatch: { $ne: null } } } },
              ],
            });
      }

      if (typeof me.hasCandidates === 'boolean') {
        selector.$and ||= [];
        selector.$and.push({
          'candidateIDs.0': { $exists: me.hasCandidates },
        });
      }

      if (me.ratingType) {
        selector.$and ||= [];
        selector.$and.push({ ratingType: me.ratingType });
      }

      if (me.examCenterID) {
        selector.$and ||= [];
        selector.$and.push({ examCenter: me.examCenterID });
      }

      if (me.locationID) {
        selector.$and ||= [];
        selector.$and.push({ locationID: me.locationID });
      }
    }

    return selector;
  }
}
